import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Flex,
    Input,
    Text,
    Box,
} from '@chakra-ui/react';
import { useState, useEffect, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import { SCREEN_MD } from 'utils/constants';
import { AddressBookEntry } from 'types/addressBook';
import { getAddressBook } from 'api/addressBookAPI';

interface AddressBookPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (address: string) => void;
}

const AddressBookPickerModal = ({ isOpen, onClose, onSelect }: AddressBookPickerModalProps) => {
    const [entries, setEntries] = useState<AddressBookEntry[]>([]);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const isScreenMedium = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

    const fetchEntries = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getAddressBook(1, 10);

            if (response?.data?.entries) {
                setEntries(response.data.entries);
            }
        } catch (e) {
            console.error('Failed to fetch address book:', e);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchEntries();
            setSearch('');
        }
    }, [isOpen, fetchEntries]);

    const filteredEntries = entries.filter(entry => {
        const q = search.toLowerCase();
        return entry.name.toLowerCase().includes(q) || entry.address.toLowerCase().includes(q);
    });

    const handleSelect = (address: string) => {
        onSelect(address);
        onClose();
    };

    return (
        <Modal onClose={onClose} isOpen={isOpen} isCentered>
            <ModalOverlay />
            <ModalContent
                maxWidth="486px"
                w="full"
                mx={isScreenMedium ? 4 : 0}
            >
                <ModalHeader pt={6} px={6} pb={0}>
                    <Flex alignItems="center" flexDirection="column">
                        <Text mt={4} size="2xl" variant="bodyBold">
                            Select Contact
                        </Text>
                    </Flex>
                    <ModalCloseButton
                        mt={2}
                        mr={2}
                        color="#565656"
                        _hover={{ border: 'none' }}
                        _focusVisible={{ outline: 'none' }}
                    />
                </ModalHeader>
                <ModalBody pb={8} pt={4} px={6}>
                    <Flex direction="column" gap={3}>
                        <Input
                            h={12}
                            borderRadius={16}
                            placeholder="Search by name or address..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                        <Box
                            maxH="320px"
                            overflowY="auto"
                            borderRadius={8}
                        >
                            {isLoading ? (
                                <Flex justify="center" py={8}>
                                    <Text size="sm" color="blackAlpha.500">Loading...</Text>
                                </Flex>
                            ) : filteredEntries.length === 0 ? (
                                <Flex justify="center" py={8}>
                                    <Text size="sm" color="blackAlpha.500">
                                        {entries.length === 0 ? 'No contacts yet' : 'No matches found'}
                                    </Text>
                                </Flex>
                            ) : (
                                filteredEntries.map((entry) => (
                                    <Box
                                        key={entry._id}
                                        p={3}
                                        cursor="pointer"
                                        borderRadius={8}
                                        _hover={{ bg: '#F9F9F9' }}
                                        onClick={() => handleSelect(entry.address)}
                                    >
                                        <Text size="sm" variant="bodyBold">
                                            {entry.name}
                                        </Text>
                                        <Text size="xs" color="blackAlpha.600">
                                            {entry.address}
                                        </Text>
                                        {entry.notes && (
                                            <Text size="xs" color="blackAlpha.400" mt={0.5} isTruncated>
                                                {entry.notes}
                                            </Text>
                                        )}
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AddressBookPickerModal;
